// src/screens/ItineraryWizard.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addDays } from 'date-fns';

import { useItinerary } from '../hooks/useItinerary';
import { obtenerItinerario } from '../api/api';
import { CURRENT_TIMING_VERSION } from '../context/ItineraryContext';

import StepProfile   from './stepProfile';
import StepDates     from './stepDates';
import styles from '../styles/itineraryAdmin.module.css';

/* ------------------------------------------------------------ */
type Step = 0 | 1;
/* ------------------------------------------------------------ */
let wizardInstanceCounter = 0;

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ItineraryWizard: React.FC = () => {
  /* ---------- contexto global ---------- */
  const {
    setItinerarios,
    setPerfil      : setPerfilCtx,
    setDias,
    setFechaInicio,
    setFechaFin,
    setTimingVersion,
    modoTransporte,
    setModoTransporte,
    resetItinerary,
  } = useItinerary();

  const navigate = useNavigate();
  const location = useLocation() as { state?: { reset?: boolean } | null };
  const instanceIdRef = useRef(++wizardInstanceCounter);
  const isWizardActiveRef = useRef(true);
  const log = (message: string, data?: unknown) => {
    if (data === undefined) {
      console.log(`[Wizard ${instanceIdRef.current}] ${message}`);
    } else {
      console.log(`[Wizard ${instanceIdRef.current}] ${message}`, data);
    }
  };

  /* ---------- estado local ------------- */
  const [step, setStep] = useState<Step>(0);

  const [perfil, setPerfilLocal] = useState('');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: new Date(),
    end  : addDays(new Date(), 1),
  });
  const [daysArray, setDaysArray] = useState<number[]>([]); // [1,2,3…]
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    isWizardActiveRef.current = true;
    log('mount/reset', { resetState: !!location.state?.reset });
    resetItinerary();
    setStep(0);
    setPerfilLocal('');
    setDateRange({
      start: new Date(),
      end: addDays(new Date(), 1),
    });
    setDaysArray([]);
    setLoading(false);
    if (location.state?.reset) {
      navigate('/', { replace: true, state: null });
    }
    return () => {
      isWizardActiveRef.current = false;
      log('unmount');
    };
    // El formulario debe arrancar limpio cada vez que se entra desde el mapa o el historial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- paso final --------------- */
  const finish = async () => {
    if (loading) return;
    setLoading(true);
    try {
      log('finish:start', {
        perfilLength: perfil.trim().length,
        days: daysArray.length,
        modoTransporte,
      });
      /* 1) guardar datos en contexto */
      setPerfilCtx(perfil);
      setDias(daysArray.length);
      const fechaInicio = formatLocalDate(dateRange.start);
      const fechaFin = formatLocalDate(dateRange.end);
      setFechaInicio(fechaInicio);
      setFechaFin(fechaFin);
      setTimingVersion(CURRENT_TIMING_VERSION);

      /* 2) pedir itinerario */
      const data = await obtenerItinerario(perfil, daysArray.length, modoTransporte);
      if (!isWizardActiveRef.current) {
        log('finish:ignoredAfterUnmount');
        return;
      }
      log('finish:itineraryReceived', {
        days: data.length,
        placesByDay: data.map((it) => it.lugares.length),
      });
      setItinerarios(data);

      /* 3) navegar al mapa */
      log('finish:navigateMap');
      navigate('/map', {
        state: {
          fechaInicio,
          fechaFin,
        },
      });
    } catch (error) {
      console.error('[Wizard] finish:error', error);
      alert('Ocurrió un error al generar el itinerario.');
    } finally {
      if (isWizardActiveRef.current) setLoading(false);
    }
  };

  /* ---------- render por paso ---------- */
  return (
    <div className={styles.wizard}>
      {/* PASO 0 · Fechas ------------------------------------------------ */}
      {step === 0 && (
        <StepDates
          dateRange={dateRange}
          setDateRange={setDateRange}
          modoTransporte={modoTransporte}
          setModoTransporte={setModoTransporte}
          onNext={(diasSeleccionados) => {
            setDaysArray(diasSeleccionados);       // [1,2,3…]
            setDias(diasSeleccionados.length);     // contexto
            setStep(1);
          }}
        />
      )}

      {/* PASO 1 · Perfil ------------------------------------------------ */}
      {step === 1 && (
        <StepProfile
          perfil={perfil}
          setPerfil={setPerfilLocal}
          onBack={() => setStep(0)}
          onNext={finish}
          loading={loading}
        />
      )}
    </div>
  );
};

export default ItineraryWizard;
